/**
 * MembershipCard.js — Wine Engine wine box subscription cards.
 *
 * OVERRIDES base MembershipCard. "Build Now" enables build-box mode at the
 * card's bottle tier and sends "Show me all available wines" (old createWineMembership).
 */

import { emit } from "../../../core/state.js";
import { getTheme } from "../../../core/configAccessors.js";
import { ASSETS_BASE_URL } from "../../../core/env.js";
import {
  AVAILABLE_WINES_MESSAGE,
  parseTierFromTitle,
} from "../features/wine-box.js";

const BOOK_ICON =
  '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.67 13V3a1.67 1.67 0 0 1 1.66-1.67H13v13.34H4.33A1.67 1.67 0 0 1 2.67 13zm0 0a1.67 1.67 0 0 1 1.66-1.67H13"/></svg>';

const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.6L6 11.8l1.4-1.4 3.4 3.4 6.2-6.2 1.4 1.4-7.6 7.6z"/></svg>';

const esc = (s) =>
  String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

export default class WineEngineMembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card ws-rich-membership ws-we-membership";

    const detailLabel =
      club.message || `Tell me more about ${club.title || "this membership"}`;
    const openDetail = () =>
      emit("ws-prompt-select", {
        label: detailLabel,
        value: detailLabel,
        uniqueId: club.unique_id,
      });

    const head = document.createElement("div");
    head.className = "ws-card-head";
    const logoPath = getTheme("card_logo_url");
    const src =
      club.cover_image_link ||
      (logoPath ? `${ASSETS_BASE_URL}${logoPath}` : null);
    if (src) {
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = src;
      if (!club.cover_image_link) img.classList.add("ws-rich-mem-logo");
      img.alt = club.title || "Wine box";
      img.loading = "lazy";
      img.style.cursor = "pointer";
      img.addEventListener("click", openDetail);
      head.appendChild(img);
    }
    card.appendChild(head);

    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = club.title;
      title.textContent = club.title;
      card.appendChild(title);
    }

    const inner = document.createElement("div");
    inner.className = "ws-rich-inner";

    if (club.price_additional_text) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = club.price_additional_text;
      inner.appendChild(price);
    }

    const bullets = Array.isArray(club.description)
      ? club.description.filter(Boolean)
      : club.description
        ? [club.description]
        : [];
    if (bullets.length) {
      const list = document.createElement("ul");
      list.className = "ws-rich-bullets";
      bullets.forEach((text) => {
        const li = document.createElement("li");
        li.innerHTML = `<span class="ws-rich-check">${CHECK_ICON}</span><span>${esc(text)}</span>`;
        list.appendChild(li);
      });
      inner.appendChild(list);
    }

    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${BOOK_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    inner.appendChild(learn);

    const showButton =
      club.display_button === undefined ? true : isTrue(club.display_button);
    if (showButton) {
      const footer = document.createElement("div");
      footer.className = "ws-rich-membership-footer";

      const pd = document.createElement("div");
      pd.className = "ws-rich-mem-price";
      const amount = document.createElement("p");
      amount.className = "ws-card-price-amount";
      amount.textContent = club.price || "";
      pd.appendChild(amount);
      const unit = document.createElement("p");
      unit.className = "ws-card-price-unit";
      unit.textContent = club.price_unit || "";
      pd.appendChild(unit);
      footer.appendChild(pd);

      const tier = parseTierFromTitle(club.title);
      const cta = document.createElement("button");
      cta.type = "button";
      cta.className = "ws-card-cta ws-we-build-now";
      cta.textContent = club.cta_text || "Build Now";
      cta.setAttribute("data-build-box", "true");
      cta.setAttribute("data-build-box-tier", String(tier));
      cta.addEventListener("click", () => {
        emit("ws-build-box-enable", { tier, frequency: "monthly" });
        emit("ws-prompt-select", {
          label: AVAILABLE_WINES_MESSAGE,
          value: AVAILABLE_WINES_MESSAGE,
          buildBox: true,
          buildBoxTier: tier,
        });
      });
      footer.appendChild(cta);
      inner.appendChild(footer);
    }

    card.appendChild(inner);
    return card;
  }
}
